import { applyPersonPose } from '../entities/person/pose.js';
/**
 * AvatarPortrait — retratos do jogador (carro atrás da pessoa, a MESMA composição do
 * banner de conquista) para os chips de "missões concluídas" do rodapé.
 *
 * Duas peças:
 *   • renderAvatarPortrait(car, person) → rasteriza UM retrato estático (canvas 2D,
 *     cópia por drawImage — sem toDataURL/PNG síncronos).
 *     Com até 5 chips simultâneos, manter um renderer WebGL vivo por chip estouraria
 *     o limite de contextos do browser — o chip normal é um <img>.
 *   • ChipCelebrations → celebrações VIVAS (várias em paralelo): cada chip novo entra
 *     com a pessoa PULANDO 3x (a mesma animação de celebração do banner/cidade) sob
 *     CONFETES (o ConfettiFX da cidade, em escala mini) e então fica parada; o Hud
 *     apenas para de redesenhar (o último quadro JÁ É o retrato estático). UM
 *     renderer WebGL só — cada frame é copiado (drawImage) p/ o canvas 2D do chip.
 *
 * Reusa as peças já montadas do banner (CAR_PARTS/PERSON_PARTS) e o buildAvatarGroup
 * do jogo → retrato idêntico ao avatar que anda pela cidade. Os materiais TINGIDOS
 * que buildAvatarGroup clona são descartados a cada troca (dispose) — sem vazamento
 * no telão de 8h.
 */
import * as THREE from 'three';
import { buildAvatarGroup, clearAvatarGroups as clearGroups } from '../entities/partsRuntime.js';
import { ConfettiFX } from '../entities/ConfettiFX.js';
import { attachContextRecovery, desistiuDoRestore } from './glRecovery.js';
import { probes, P } from '../perfProbes.js';
import { CAR_PARTS, PERSON_PARTS } from './Achievements.js';

const W = 144, H = 126; // buffer 2× o display do chip (72×63, proporção 8:7 do banner)
const JUMP_H = 0.55;    // altura do pulo da celebração (igual ao banner)
const HOPS = 3;         // nº de pulos na entrada do chip — depois fica parado
const LIVE_DUR = 3.05;  // s de vida do retrato VIVO (cobre os pulos + confete ~3s)
// Chips a 30 Hz. Cada chip vivo custa ~36 draw calls + um drawImage WebGL→2D por
// quadro; 5 simultâneos a 60 fps eram ~180 draw calls/quadro durante os 3 s da
// rajada. A 30 Hz o pulo (3 saltos em 3 s) continua liso.
const CHIP_HZ = 30;
// Falha ao criar um renderer auxiliar fica memorizada por ESTE intervalo antes de
// permitir nova tentativa. Sem memorizar, uma máquina sem WebGL pagava uma criação
// de renderer por chip; memorizar PARA SEMPRE era pior: uma falha TRANSITÓRIA
// (TDR/driver reiniciando bem na hora) degradava os retratos pelas 8 h restantes.
const FAIL_RETRY_MS = 30000;
const CHIP_DT = 1 / CHIP_HZ;

/* ------------------------- helpers compartilhados ------------------------- */


function makeScene() {
  const scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight(0xffffff, 0x2a3a30, 1.2));
  const dir = new THREE.DirectionalLight(0xffffff, 1.6);
  dir.position.set(2.5, 4, 3);
  scene.add(dir);
  const cam = new THREE.PerspectiveCamera(30, W / H, 0.1, 100);
  const inner = new THREE.Group();
  scene.add(inner);
  return { scene, cam, inner };
}

/** Monta a composição do banner: pessoa herói + carro 3/4 atrás. */
function compose(inner, carAppearance, personAppearance) {
  const person = buildAvatarGroup(PERSON_PARTS, personAppearance);
  const car = buildAvatarGroup(CAR_PARTS, carAppearance);
  car.scale.setScalar(0.62);
  car.position.set(-0.55, 0, -2.3);
  car.rotation.y = -0.5; // 3/4 atrás
  person.position.set(0.6, 0, 0.4);
  person.rotation.y = 0.1;
  inner.add(car, person);
  return { car, person };
}

/** Mede a caixa da composição (com o inner zerado na origem). */
function measure(scene, inner) {
  inner.position.set(0, 0, 0);
  scene.updateMatrixWorld(true);
  return new THREE.Box3().setFromObject(inner);
}

/** Parâmetros de enquadramento (centro + distância da câmera) p/ uma caixa.
 *  `headroom` reserva espaço no topo p/ o pulo (a cabeça não corta). Separado de
 *  applyFrame p/ o retrato vivo poder INTERPOLAR entre dois enquadramentos (com e
 *  sem headroom) — o zoom suave pós-pulo que casa com a imagem estática. */
function frameParams(cam, box, headroom = 0) {
  const cy = (box.min.y + box.max.y + headroom) / 2;
  const cx = (box.min.x + box.max.x) / 2;
  const cz = (box.min.z + box.max.z) / 2;
  const sy = (box.max.y + headroom) - box.min.y;
  const sx = box.max.x - box.min.x;
  const sz = box.max.z - box.min.z;
  const fov = (cam.fov * Math.PI) / 180;
  const fit = Math.max(sy, sx / cam.aspect) * 1.18;
  let dist = fit / 2 / Math.tan(fov / 2) + sz / 2;
  dist /= 1.05;
  return { cx, cy, cz, dist };
}

/** Aplica um enquadramento: centraliza o inner e posiciona a câmera. */
function applyFrame(cam, inner, p) {
  inner.position.set(-p.cx, -p.cy, -p.cz);
  cam.position.set(0, p.dist * 0.1, p.dist);
  cam.lookAt(0, 0, 0);
}

/** Enquadra de uma vez (mede + aplica) — caminho do retrato estático. */
function frame(cam, scene, inner, headroom = 0) {
  applyFrame(cam, inner, frameParams(cam, measure(scene, inner), headroom));
}

/* --------------------------- retrato ESTÁTICO ---------------------------- */

let _r = null, _st = null;

const _staticOwner = { _lost: false, _failedAt: 0 };

function ensureStatic() {
  if (_staticOwner._lost) {
    if (!desistiuDoRestore(_staticOwner)) return false;
    // o restore não veio (canvas offscreen) → joga fora e recomeça com um canvas novo
    try { _r?.dispose(); } catch { /* já morto */ }
    _r = null; _st = null;
    _staticOwner._lost = false; _staticOwner._lostAt = 0;
  }
  if (_staticOwner._failedAt && performance.now() - _staticOwner._failedAt < FAIL_RETRY_MS) return false;
  if (_r) return true;
  try {
    const canvas = document.createElement('canvas');
    _r = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    _r.setSize(W, H, false);
    attachContextRecovery(canvas, _staticOwner, () => _r.setSize(W, H, false));
  } catch (e) {
    // memoriza COM expiração (FAIL_RETRY_MS): protege contra re-tentativa por chip
    // numa máquina sem WebGL, mas uma pane transitória não vira degradação de 8 h
    _staticOwner._failedAt = performance.now();
    return false; // caller usa o fallback (dot de cor)
  }
  _st = makeScene();
  return true;
}

/**
 * Renderiza o retrato uma vez e devolve um CANVAS 2D próprio (não um dataURL).
 *
 * A versão anterior devolvia `toDataURL('image/png')`: leitura do framebuffer +
 * codificação PNG, as duas síncronas na thread principal — exatamente o pico que
 * este caminho de fallback existia para evitar. O `drawImage` de um canvas WebGL
 * para um canvas 2D é uma cópia composta pelo browser (sem readback nem PNG), e o
 * canvas devolvido é um elemento: o chip usa direto, sem decodificar imagem.
 * (ImageBitmap/Blob assíncronos foram avaliados: adiam o visual do fallback em
 * 1+ quadros e complicam o assentamento do chip — a cópia direta já tira o custo
 * dominante.)
 */
export function renderAvatarPortrait(carAppearance, personAppearance) {
  if (!carAppearance && !personAppearance) return null;
  if (!ensureStatic()) return null;
  clearGroups(_st.inner);
  compose(_st.inner, carAppearance, personAppearance);
  frame(_st.cam, _st.scene, _st.inner);
  _r.render(_st.scene, _st.cam);
  try {
    // contexto perdido não LANÇA: o drawImage sairia transparente e o chip ficaria
    // vazio permanente. Na janela até o evento chegar o flag _lost ainda é false —
    // por isso a pergunta direta ao contexto.
    if (_r.getContext().isContextLost()) return null;
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    c.getContext('2d').drawImage(_r.domElement, 0, 0);
    return c;
  } catch (e) {
    return null;
  }
}

/* ------------------------ celebrações VIVAS (chips) ------------------------ */
/**
 * VÁRIOS chips podem comemorar ao MESMO tempo (rajada de conquistas): um chip
 * arrastado p/ a posição 2 continua pulando até o fim. Para não estourar o limite
 * de contextos WebGL do browser (1 por canvas), usamos UM renderer offscreen e, a
 * cada frame, desenhamos cada celebração e COPIAMOS (drawImage) para o canvas 2D
 * do chip — o padrão "vários thumbnails 3D, um só contexto GL" do three.js.
 */
export class ChipCelebrations {
  constructor() {
    this._r = null;      // renderer compartilhado (lazy; null definitivo se sem WebGL)
    this._items = [];    // celebrações ativas
    this._free = [];     // cenas prontas p/ reuso (luzes/câmera/confetti montados)
  }

  _ensure() {
    if (this._lost) {
      if (!desistiuDoRestore(this)) return false; // sem contexto: nada de desenhar até restaurar
      try { this._r?.dispose(); } catch { /* já morto */ }
      this._r = null;                             // canvas novo no passo abaixo
      this._lost = false; this._lostAt = 0;
    }
    if (this._r) return true;
    if (this._failedAt && performance.now() - this._failedAt < FAIL_RETRY_MS) return false;
    try {
      const canvas = document.createElement('canvas');
      this._r = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
      this._r.setSize(W, H, false);
      attachContextRecovery(canvas, this, () => {
        // ao voltar: as celebrações que estavam no ar já foram encerradas, e o three
        // recria os recursos de GPU sob demanda no próximo render. Nada a reconstruir.
        this._r.setSize(W, H, false);
      });
    } catch (e) {
      this._failedAt = performance.now(); // com expiração — ver FAIL_RETRY_MS
      return false;
    }
    return true;
  }

  _takeScene() {
    if (this._free.length) return this._free.pop();
    const s = makeScene();
    s.confetti = new ConfettiFX();
    s.scene.add(s.confetti.mesh); // em espaço da CENA (não segue o inner)
    return s;
  }

  /** Começa a celebração de um chip (pessoa pula 3x + confete → fica parada).
   *  Devolve o item — `item.canvas` é o <canvas> 2D a colocar no chip e `item.done`
   *  indica o fim — ou null (sem WebGL/aparência) → caller usa a imagem estática. */
  start(carAppearance, personAppearance) {
    if (!carAppearance && !personAppearance) return null;
    if (!this._ensure()) return null;
    const s = this._takeScene();
    clearGroups(s.inner);
    const { person } = compose(s.inner, carAppearance, personAppearance);
    // DOIS enquadramentos: com folga p/ o pulo (durante a celebração) e o da imagem
    // ESTÁTICA (sem folga) — após o último pouso a câmera faz zoom suave de A → B,
    // então a troca canvas→imagem no fim é invisível (sem "pulo" de tamanho).
    const box = measure(s.scene, s.inner);
    const pA = frameParams(s.cam, box, JUMP_H);
    const pB = frameParams(s.cam, box, 0);
    applyFrame(s.cam, s.inner, pA);
    // confete estoura na altura da cabeça e assenta no CHÃO do retrato (pés do
    // avatar = plano y do inner na cena) — lento p/ ficar no quadro; TAMANHO padrão
    // da cidade (mesma proporção confete/avatar do jogo)
    const p = s.inner.position;
    s.confetti.mesh.position.set(0, 0, 0);
    s.confetti.play(p.x + person.position.x, p.y + 1.6, p.z + person.position.z, {
      speed: 0.4, floorY: p.y + 0.02
    });
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const item = {
      canvas, ctx: canvas.getContext('2d'), s, person, pA, pB,
      animMeshes: person.children.filter((m) => m.userData.part && m.userData.part.anim),
      t: 0, done: false
    };
    this._items.push(item);
    this._draw(item, 0);
    return item;
  }

  /**
   * PRÉ-AQUECIMENTO (chamado no ocioso, depois do boot). Sem isto, a PRIMEIRA
   * conquista da sessão pagava, no mesmo quadro: criar o contexto WebGL, compilar
   * os programas de shader das peças e do confete, e alocar a cena/confete. É o
   * maior pico do caminho — e some quando acontece antes, na ociosidade.
   *
   * Roda o caminho REAL (start + release) porque é o que de fato compila os
   * programas; o canvas gerado nunca entra no DOM.
   */
  prewarm(pares) {
    if (!this._ensure()) return false;
    for (const [carApp, personApp] of pares) {
      const item = this.start(carApp, personApp);
      if (item) this.release(item);
    }
    return true;
  }

  /** Encerra uma celebração antes da hora (chip saiu por cap/reconquista). */
  release(item) {
    if (!item || item.done) return;
    item.done = true;
    this._recycle(item);
    const i = this._items.indexOf(item);
    if (i >= 0) this._items.splice(i, 1);
  }

  /** Encerra tudo que estava no ar — usado quando o contexto WebGL se perde. O canvas
   *  de um chip interrompido NÃO é confiável (pode ter ficado em branco: drawImage de
   *  um canvas perdido pinta transparente, e na janela até o evento chegar isso pode
   *  já ter acontecido) — por isso cada item sai marcado `aborted`, e o Hud troca o
   *  canvas pelo visual estático (retrato rasterizado ou dot). */
  _abortAll() {
    for (const item of this._items) {
      item.done = true;
      item.aborted = true; // interrompida (≠ terminou): o Hud troca o canvas pelo fallback
      this._recycle(item);
    }
    this._items.length = 0;
  }

  /** Avança TODAS as celebrações 1 frame (chamado pelo Hud a cada frame). */
  update(dt) {
    if (this._lost) return 0;
    let desenhados = 0;
    for (let i = this._items.length - 1; i >= 0; i--) {
      const item = this._items[i];
      item.t += dt;
      // desenha a CHIP_HZ; o acumulador guarda o dt real para a física do pulo e
      // do confete continuar correta mesmo desenhando menos vezes
      item.acc = (item.acc || 0) + dt;
      if (item.acc >= CHIP_DT || item.t >= LIVE_DUR) {
        this._draw(item, item.acc);
        item.acc = 0;
        desenhados++;
      }
      if (item.t >= LIVE_DUR) {
        item.done = true; // o Hud vê e congela/assenta o canvas
        this._recycle(item);
        this._items.splice(i, 1);
      }
    }
    return desenhados; // p/ o chamador DEFASAR o banner (não 2 renders aux no mesmo quadro)
  }

  _recycle(item) {
    item.s.confetti.hide();
    clearGroups(item.s.inner); // dispose dos materiais tingidos deste retrato
    this._free.push(item.s);
  }

  _draw(item, dt) {
    probes.begin(P.auxGL);
    // Janela de corrida de um TDR: o contexto já está perdido mas o evento ainda não
    // chegou (chega num task posterior). O render vira no-op, mas o clearRect +
    // drawImage abaixo APAGARIAM o canvas 2D do chip. Mantém os últimos pixels
    // válidos; o _abortAll assenta em seguida.
    if (this._r.getContext().isContextLost()) { probes.end(P.auxGL); return; }
    // MESMA celebração do banner (_celebrate): pulo+squash+braços — limitada a HOPS
    // pulos (fase de |sin| = π por pulo); depois a pessoa fica PARADA (pose neutra).
    const person = item.person;
    const ph = item.t * 4.5;
    if (ph < Math.PI * HOPS) {
      const j = Math.abs(Math.sin(ph));
      const swing = Math.sin(ph * 2) * 0.12;
      const wave = 1.9 + j * 0.5 + Math.sin(item.t * 9) * 0.12;
      const sy = 1 - 0.18 * Math.pow(1 - j, 3);
      person.scale.setScalar(1);
      person.position.y = j * JUMP_H + this._pose(item, swing, wave, sy);
    } else {
      person.position.y = 0;
      person.scale.set(1, 1, 1);
      this._pose(item, 0, 0); // braços/pernas de volta à pose neutra → "parado"
      // pós-pouso: zoom suave do enquadramento do pulo (A) p/ o da imagem estática
      // (B) — no fim, o frame vivo é IGUAL ao <img> que o substitui (sem quebra).
      const k = Math.min(1, (item.t - (Math.PI * HOPS) / 4.5) / 0.45);
      const e = k * k * (3 - 2 * k); // smoothstep
      const { pA, pB } = item;
      applyFrame(item.s.cam, item.s.inner, {
        cx: pA.cx + (pB.cx - pA.cx) * e,
        cy: pA.cy + (pB.cy - pA.cy) * e,
        cz: pA.cz + (pB.cz - pA.cz) * e,
        dist: pA.dist + (pB.dist - pA.dist) * e
      });
      // os confetes (em espaço da cena) acompanham o deslocamento do avatar no zoom
      item.s.confetti.mesh.position.y = (pA.cy - pB.cy) * e;
    }
    item.s.confetti.update(dt);
    this._r.render(item.s.scene, item.s.cam);
    // copia o frame renderizado p/ o canvas 2D do chip (mesma task → buffer válido)
    item.ctx.clearRect(0, 0, W, H);
    item.ctx.drawImage(this._r.domElement, 0, 0);
    probes.end(P.auxGL);
  }

  /** Aplica swing/wave às peças articuladas (mesma conta do banner/Fleet). */
  _pose(item, swing, wave, squashY=1) {
    return applyPersonPose(item.person, {swing,waveZ:wave,waveZL:wave,squashY});
  }
}
